import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ProjeAyarlariListesi from './ProjeAyarlariListesi';
import DaireTipleri from './DaireTipleri';
import Katlar from './Katlar';
import Cepheler from './Cepheler';
import Manzaralar from './Manzaralar';
import ParaBirimleri from './ParaBirimleri';
import Kasalar from './Kasalar';
import FinansKategorileri from './FinansKategorileri';
import CariAltKategorileri from './CariAltKategorileri';
import BankaKredileri from './BankaKredileri';
import EnflasyonOranlari from './EnflasyonOranlari';
import KullaniciYonetimi from './KullaniciYonetimi';
import KurumsalBilgiler from './KurumsalBilgiler';
import DokumanSablonlari from './DokumanSablonlari';
import IslemKayitlari from './IslemKayitlari';
import LeadStatusAyarlari from './LeadStatusAyarlari';
import LeadApiAyarlariPage from './LeadApiAyarlari';
import PazarlamaAraclari from './PazarlamaAraclari';
import GunSonuRaporu from './GunSonuRaporu';
import OdemePlanSablonlari from './OdemePlanSablonlari';
import Etiketler from './Etiketler';
import SmsAyarlariPage from './SmsAyarlari';
import SmsSablonlariPage from './SmsSablonlari';
import IysAktarimAyarlariPage from './IysAktarimAyarlari';
import OtomatikBildirimKurallari from './OtomatikBildirimKurallari';
import DilYonetimi from './DilYonetimi';
import DisaAktar from './DisaAktar';
import GayrimenkulFiyatlama from './GayrimenkulFiyatlama';
import YapayZekaAyarlari from './YapayZekaAyarlari';
import WhatsAppAyarlariPage from './WhatsAppAyarlari';

interface AyarItem {
    baslik: string;
    aciklama: string;
    icon: string;
    yol: string;
    component: React.FC;
}

interface AyarGrubu {
    baslik: string;
    icon: string;
    aciklama: string;
    renkSinifi: 'turuncu' | 'lacivert' | 'yesil';
    ogeler: AyarItem[];
}

const Ayarlar: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    // Diğer sayfalardan (ör. Proje Detayı'ndaki kurulum yönlendirmesi) doğrudan bir
    // sekme/öge açık gelsin diye location.state ile başlangıç değeri verilebilir.
    const location = useLocation();
    const baslangicState = location.state as { grup?: number; item?: number } | null;
    const [activeTab, setActiveTab] = useState(baslangicState?.grup ?? 0);
    const [activeItem, setActiveItem] = useState(baslangicState?.item ?? 0);

    const gruplar: AyarGrubu[] = [
        {
            baslik: t('ayarlar.projeTanimlari'),
            icon: 'fas fa-drafting-compass',
            aciklama: 'Projeler, daire tipleri, katlar, cepheler ve manzara tanımlamaları',
            renkSinifi: 'turuncu',
            ogeler: [
                { baslik: t('proje.baslik'), aciklama: 'Proje listesi ve yönetimi', icon: 'fas fa-building', yol: '/projeler', component: ProjeAyarlariListesi },
                { baslik: t('ayarlar.daireTipleri'), aciklama: 'Daire tip tanımlamaları', icon: 'fas fa-door-closed', yol: '/tanimlamalar/daire-tipleri', component: DaireTipleri },
                { baslik: t('ayarlar.katlar'), aciklama: 'Kat tanımlamaları', icon: 'fas fa-layer-group', yol: '/tanimlamalar/katlar', component: Katlar },
                { baslik: t('ayarlar.cepheler'), aciklama: 'Cephe yön tanımlamaları', icon: 'fas fa-compass', yol: '/tanimlamalar/cepheler', component: Cepheler },
                { baslik: t('ayarlar.manzaralar'), aciklama: 'Manzara tanımlamaları', icon: 'fas fa-mountain', yol: '/tanimlamalar/manzaralar', component: Manzaralar },
            ],
        },
        {
            baslik: t('ayarlar.finansalTanimlar'),
            icon: 'fas fa-coins',
            aciklama: 'Para birimleri, kasalar, gelir/gider kategorileri ve banka tanımlamaları',
            renkSinifi: 'lacivert',
            ogeler: [
                { baslik: t('ayarlar.paraBirimleri'), aciklama: 'Kullanılan para birimleri', icon: 'fas fa-money-bill-wave', yol: '/tanimlamalar/para-birimleri', component: ParaBirimleri },
                { baslik: t('kasa.baslik'), aciklama: 'Kasa hesapları', icon: 'fas fa-cash-register', yol: '/tanimlamalar/kasalar', component: Kasalar },
                { baslik: t('ayarlar.gelirGiderKategorileri', 'Gelir/Gider Kategorileri'), aciklama: 'Finansal işlem kategorileri', icon: 'fas fa-tags', yol: '/tanimlamalar/finans-kategorileri', component: FinansKategorileri },
                { baslik: t('menu.cariAltKategorileri', 'Cari Alt Kategorileri'), aciklama: 'Cari hesap alt grupları', icon: 'fas fa-sitemap', yol: '/tanimlamalar/cari-alt-kategorileri', component: CariAltKategorileri },
                { baslik: t('ayarlar.bankaKredileri'), aciklama: 'Banka kredi tanımlamaları', icon: 'fas fa-university', yol: '/tanimlamalar/banka-kredileri', component: BankaKredileri },
                { baslik: t('ayarlar.enflasyonOranlari', 'Enflasyon Oranları'), aciklama: 'TÜFE / Yİ-ÜFE — kira artış referansı', icon: 'fas fa-chart-line', yol: '/tanimlamalar/enflasyon-oranlari', component: EnflasyonOranlari },
            ],
        },
        {
            baslik: t('ayarlar.finansalIslemler', 'Finansal İşlemler'),
            icon: 'fas fa-hand-holding-usd',
            aciklama: 'Gayrimenkul fiyatlama ve vadeye bağlı fiyat kuralları',
            renkSinifi: 'yesil',
            ogeler: [
                { baslik: t('ayarlar.gayrimenkulFiyatlama', 'Gayrimenkul Fiyatlama'), aciklama: 'Vadeye bağlı fiyat artışı kuralları', icon: 'fas fa-chart-line', yol: '/tanimlamalar/gayrimenkul-fiyatlama', component: GayrimenkulFiyatlama },
            ],
        },
        {
            baslik: t('ayarlar.sistemAyarlari', 'Sistem Ayarları'),
            icon: 'fas fa-cog',
            aciklama: 'Kullanıcı yönetimi, kurumsal bilgiler ve döküman şablonları',
            renkSinifi: 'yesil',
            ogeler: [
                ...(user?.kullaniciRolu === 'Admin'
                    ? [{ baslik: t('menu.kullaniciYonetimi', 'Kullanıcı Yönetimi'), aciklama: 'Kullanıcı hesapları ve roller', icon: 'fas fa-users-cog', yol: '/tanimlamalar/kullanici-yonetimi', component: KullaniciYonetimi as React.FC }]
                    : []),
                { baslik: t('ayarlar.kurumsal.baslik', 'Kurumsal Bilgiler'), aciklama: 'Firma adı, logo ve iletişim', icon: 'fas fa-id-card', yol: '/tanimlamalar/kurumsal-bilgiler', component: KurumsalBilgiler },
                { baslik: t('ayarlar.dokumanSablonlari', 'Döküman Şablonları'), aciklama: 'PDF ve belge şablonları', icon: 'fas fa-file-alt', yol: '/tanimlamalar/dokuman-sablonlari', component: DokumanSablonlari },
                { baslik: t('ayarlar.islemKayitlari', 'İşlem Kayıtları'), aciklama: 'Sistem işlem geçmişi', icon: 'fas fa-history', yol: '/islem-kayitlari', component: IslemKayitlari },
                { baslik: t('ayarlar.gunSonu.baslik', 'Gün Sonu Raporu'), aciklama: 'Otomatik e-posta rapor ayarları', icon: 'fas fa-envelope-open-text', yol: '/tanimlamalar/gun-sonu-raporu', component: GunSonuRaporu },
                { baslik: t('ayarlar.odemePlanSablonlari', 'Ödeme Planı Şablonları'), aciklama: 'Daire satışı için peşinat ve taksit kuralları', icon: 'fas fa-file-invoice-dollar', yol: '/tanimlamalar/odeme-plan-sablonlari', component: OdemePlanSablonlari as React.FC },
                { baslik: t('ayarlar.etiket.baslik', 'Etiketler'), aciklama: 'Kayıtlara atanabilecek etiket tanımlamaları', icon: 'fas fa-tags', yol: '/tanimlamalar/etiketler', component: Etiketler },
                { baslik: t('ayarlar.smsAyarlari', 'SMS Ayarları'), aciklama: 'SMS gönderim servisi ve borç hatırlatma ayarları', icon: 'fas fa-sms', yol: '/tanimlamalar/sms-ayarlari', component: SmsAyarlariPage },
                { baslik: t('ayarlar.smsSablonlari', 'SMS Şablonları'), aciklama: 'Hazır ve özel SMS mesaj şablonları', icon: 'fas fa-comment-alt', yol: '/tanimlamalar/sms-sablonlari', component: SmsSablonlariPage },
                { baslik: t('ayarlar.iysAktarimAyarlari', 'İYS Aktarım Ayarları'), aciklama: 'NetGSM üzerinden İYS izni aktarım ayarları', icon: 'fas fa-share-alt', yol: '/tanimlamalar/iys-aktarim-ayarlari', component: IysAktarimAyarlariPage },
                { baslik: t('ayarlar.otomatikBildirimKurallari', 'Otomatik Bildirim Kuralları'), aciklama: 'Vadeli ödemeler, sunumlar, kira sözleşmeleri ve lead takip bildirimleri', icon: 'fas fa-robot', yol: '/tanimlamalar/otomatik-bildirim-kurallari', component: OtomatikBildirimKurallari as React.FC },
                { baslik: t('ayarlar.yapayZekaAyarlari', 'Yapay Zeka Ayarları'), aciklama: 'Akıllı Arama için AI sağlayıcı ve API anahtarı', icon: 'fas fa-brain', yol: '/tanimlamalar/yapay-zeka-ayarlari', component: YapayZekaAyarlari },
                { baslik: t('ayarlar.whatsappAyarlari', 'WhatsApp Ayarları'), aciklama: 'Evolution API v2 bağlantı ve otomatik bildirim ayarları', icon: 'fab fa-whatsapp', yol: '/tanimlamalar/whatsapp-ayarlari', component: WhatsAppAyarlariPage },
                { baslik: t('ayarlar.dilYonetimi', 'Dil Yönetimi'), aciklama: 'Uygulama dillerini ve çevirilerini yönetin', icon: 'fas fa-language', yol: '/tanimlamalar/dil-yonetimi', component: DilYonetimi },
                ...(user?.kullaniciRolu === 'Admin'
                    ? [{ baslik: t('ayarlar.disaAktar', 'Dışa Aktar'), aciklama: 'Verilerinizi Excel ve PDF formatında dışa aktarın', icon: 'fas fa-file-export', yol: '/tanimlamalar/disa-aktar', component: DisaAktar as React.FC }]
                    : []),
            ],
        },
        {
            baslik: t('ayarlar.potansiyelAyarlar', 'Potansiyel Müşteri Ayarları'),
            icon: 'fas fa-user-tag',
            aciklama: 'Potansiyel müşteri durum tanımlamaları ve takip ayarları',
            renkSinifi: 'turuncu',
            ogeler: [
                { baslik: t('ayarlar.leadDurumlari', 'Lead Durumları'), aciklama: 'Durum tanımlamaları, kanban ve liste ayarları', icon: 'fas fa-flag', yol: '/tanimlamalar/lead-durumlari', component: LeadStatusAyarlari },
                { baslik: t('ayarlar.pazarlamaAraclari', 'Pazarlama Araçları'), aciklama: 'Müşteri kaynak seçenekleri (telefon, web, fuar vb.)', icon: 'fas fa-megaphone', yol: '/tanimlamalar/pazarlama-araclari', component: PazarlamaAraclari },
                { baslik: t('ayarlar.disKaynakApi', 'Dış Kaynak API'), aciklama: 'Web sitesi ve form entegrasyonu için API yönetimi', icon: 'fas fa-plug', yol: '/tanimlamalar/lead-api', component: LeadApiAyarlariPage as React.FC },
            ],
        },
    ];

    const activeGrup = gruplar[activeTab];
    const selectedOge = activeGrup.ogeler[activeItem] ?? activeGrup.ogeler[0];

    const handleTabChange = (i: number) => {
        setActiveTab(i);
        setActiveItem(0);
    };

    return (
        <div style={{ padding: '28px 32px', background: '#f4f6fb', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
            {/* Sayfa başlığı */}
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>{t('ayarlar.baslik')}</h1>
                <p style={{ fontSize: 13.5, color: '#64748b', margin: '4px 0 0' }}>{t('ayarlar.aciklama')}</p>
            </div>

            {/* Ana Kart */}
            <div style={{
                background: '#fff',
                borderRadius: 14,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                overflow: 'hidden',
            }}>
                {/* Sekme Başlıkları */}
                <div style={{
                    borderBottom: '1px solid #e5e7eb',
                    padding: '0 24px',
                    display: 'flex',
                    flexWrap: 'wrap',
                }}>
                    {gruplar.map((g, i) => {
                        const isActive = i === activeTab;
                        return (
                            <button
                                key={g.baslik}
                                onClick={() => handleTabChange(i)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '14px 18px',
                                    border: 'none',
                                    borderBottom: isActive ? '2px solid #cd6906' : '2px solid transparent',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: 13.5,
                                    fontWeight: isActive ? 600 : 500,
                                    color: isActive ? '#cd6906' : '#4b5563',
                                    transition: 'all 0.15s',
                                    whiteSpace: 'nowrap',
                                    marginBottom: -1,
                                }}
                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#111827'; }}
                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#4b5563'; }}
                            >
                                <i className={g.icon} style={{ fontSize: 13, opacity: isActive ? 1 : 0.7 }}></i>
                                {g.baslik}
                                <span style={{
                                    background: isActive ? 'rgba(205,105,6,0.12)' : '#f3f4f6',
                                    color: isActive ? '#cd6906' : '#6b7280',
                                    borderRadius: 20,
                                    padding: '1px 8px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                }}>
                                    {g.ogeler.length}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* İki Kolonlu İçerik */}
                <div style={{ display: 'flex', minHeight: 420 }}>
                    {/* Sol Menü */}
                    <div style={{
                        width: 240,
                        borderRight: '1px solid #e5e7eb',
                        flexShrink: 0,
                        padding: '12px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}>
                        <div style={{ padding: '6px 12px 10px', fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            {activeGrup.baslik}
                        </div>
                        {activeGrup.ogeler.map((oge, i) => {
                            const isSelected = i === activeItem;
                            return (
                                <button
                                    key={oge.yol}
                                    onClick={() => setActiveItem(i)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '10px 12px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: isSelected ? 'rgba(205,105,6,0.08)' : 'none',
                                        color: isSelected ? '#cd6906' : '#374151',
                                        fontSize: 13,
                                        fontWeight: isSelected ? 600 : 500,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        width: '100%',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.color = '#111827'; } }}
                                    onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#374151'; } }}
                                >
                                    <span style={{
                                        width: 30, height: 30, borderRadius: 7,
                                        background: isSelected ? 'rgba(205,105,6,0.12)' : '#f3f4f6',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0, transition: 'all 0.15s',
                                    }}>
                                        <i className={oge.icon} style={{ fontSize: 13, color: isSelected ? '#cd6906' : '#6b7280' }}></i>
                                    </span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{oge.baslik}</span>
                                    {isSelected && (
                                        <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: 10, color: '#cd6906', flexShrink: 0 }}></i>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Sağ Panel - Component direkt render */}
                    <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
                        {selectedOge && (() => {
                            const SelectedComponent = selectedOge.component;
                            return <SelectedComponent />;
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Ayarlar;
